import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getSupplierSuggestions,
  hideSupplierSuggestion,
  type BulkSupplierInput,
} from '../../../shared/api/procurementRequests.api'
import { SupplierDuplicateHint } from '../../suppliers/components/SupplierDuplicateHint'

// Попап выбора поставщиков перед скачиванием BOM. Показывает список ранее
// встречавшихся поставщиков (из квот + предыдущих запросов) с чекбоксами,
// плюс поле для ввода нового поставщика. Закупщик может отметить нескольких
// сразу — система создаст несколько запросов и скачает несколько XLSX-файлов
// с уникальными именами (`STRONG-20260507-001.xlsx` / `WTR-20260507-002.xlsx`).
//
// Дедуп — на бэке (case-insensitive). Здесь дополнительно фильтруем кнопкой
// «Очистить» если пользователь промахнулся.

// Единый ключ имени поставщика: trim + lowercase. Используется ВЕЗДЕ (выбор,
// скрытие, дедуп) — иначе имя с пробелами по краям («Lera / Taobao ») даёт
// разные ключи в разных местах и крестик/чекбокс не срабатывают.
const keyOf = (name: string) => name.trim().toLowerCase()

export function SupplierSelectModal({
  itemsCount,
  onClose,
  onConfirm,
  onDownloadNoSupplier,
  initialSuppliers,
}: {
  itemsCount: number
  onClose: () => void
  onConfirm: (
    suppliers: BulkSupplierInput[],
    note: string | null,
    // Прямой запрос производителю (эксклюзив канала per-позиция) — имя из
    // блока «Производитель» либо null.
    manufacturer: string | null,
  ) => Promise<void> | void
  // Скачать Excel без поставщика — файл «ноунейм», без записи на бэке и
  // без смены статусов позиций.
  onDownloadNoSupplier: () => Promise<void> | void
  // Поставщики, отмеченные при открытии (например, владелец запроса в
  // «Скачать остатки»). Показываются в списке, даже если их нет в подсказках.
  initialSuppliers?: string[]
}) {
  const queryClient = useQueryClient()
  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ['supplier-suggestions'],
    queryFn: getSupplierSuggestions,
    staleTime: 30_000,
  })

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set((initialSuppliers ?? []).map(keyOf)),
  )
  const [newSupplier, setNewSupplier] = useState('')
  const [extras, setExtras] = useState<string[]>([])
  // Скрытые из списка поставщики (lowercase key). Скрытие локальное — сам
  // поставщик и его квоты не трогаются, при следующем открытии модалки
  // suggestion вернётся (грузится с бэка).
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  // Блок «Производитель»: имя производителя для прямого запроса (claim).
  const [manufacturer, setManufacturer] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [search, setSearch] = useState('')

  const allOptions = useMemo(() => {
    const fromSuggestions = suggestions.map((s) => s.name)
    // Соединяем существующих + добавленных вручную; не показываем дубликаты
    // (case-insensitive) и скрытые крестиком. extras сверху — пользователь
    // только что добавил их. initialSuppliers — после них, чтобы предвыбранный
    // поставщик был виден даже если его нет в серверных подсказках.
    const seen = new Set<string>()
    const out: string[] = []
    for (const name of [...extras, ...(initialSuppliers ?? []), ...fromSuggestions]) {
      const key = keyOf(name)
      if (!key || seen.has(key) || hidden.has(key)) continue
      seen.add(key)
      out.push(name)
    }
    return out
  }, [suggestions, extras, hidden, initialSuppliers])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return allOptions
    return allOptions.filter((n) => n.toLowerCase().includes(q))
  }, [allOptions, search])

  // ESC — закрыть.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, submitting])

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      const key = keyOf(name)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Убирает поставщика из списка: скрываем локально (мгновенно), снимаем
  // выбор, выкидываем из extras. Если поставщик есть в серверных подсказках —
  // дополнительно скрываем его навсегда через API, чтобы он не вернулся при
  // следующем открытии окна. Чистый extra (только что введённое имя, ещё
  // нет в квотах/запросах) скрывать на бэке не нужно — его там и так нет.
  const removeFromList = (name: string) => {
    const key = keyOf(name)
    setHidden((prev) => new Set(prev).add(key))
    setSelected((prev) => {
      if (!prev.has(key)) return prev
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    setExtras((prev) => prev.filter((e) => keyOf(e) !== key))

    const inSuggestions = suggestions.some((s) => keyOf(s.name) === key)
    if (inSuggestions) {
      void hideSupplierSuggestion(name).then(() => {
        void queryClient.invalidateQueries({ queryKey: ['supplier-suggestions'] })
      })
    }
  }

  // name — имя из подсказки «похоже на WTR — это он?»: вместо введённого
  // отмечаем существующего поставщика (дубль не заводится).
  const addExtra = (name = newSupplier) => {
    const trimmed = name.trim()
    if (!trimmed) return
    const key = keyOf(trimmed)
    // Если поставщик был скрыт крестиком — возвращаем его в список.
    if (hidden.has(key)) {
      setHidden((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
    // Если такой уже есть в suggestions или в extras — просто отмечаем чекбокс.
    if (allOptions.some((n) => keyOf(n) === key)) {
      setSelected((prev) => new Set(prev).add(key))
    } else {
      setExtras((prev) => [trimmed, ...prev])
      setSelected((prev) => new Set(prev).add(key))
    }
    setNewSupplier('')
  }

  const submit = async () => {
    // Имена возвращаем в исходном написании (как пользователь видит в списке),
    // не lowercase ключи. Бэк дополнительно дедупит case-insensitive.
    const names = allOptions.filter((n) => selected.has(keyOf(n)))
    const manufacturerName = manufacturer.trim() || null
    if (names.length === 0 && !manufacturerName) return
    setSubmitting(true)
    try {
      await onConfirm(
        names.map((name) => ({ name })),
        null,
        manufacturerName,
      )
    } finally {
      setSubmitting(false)
    }
  }

  const downloadNoSupplier = async () => {
    setSubmitting(true)
    try {
      await onDownloadNoSupplier()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Кому отправляем запрос?</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Позиций в запросе: {itemsCount}. Можно выбрать несколько поставщиков — на каждого
              сгенерируется отдельный файл.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none disabled:opacity-50"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={newSupplier}
              onChange={(e) => setNewSupplier(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addExtra()
                }
              }}
              placeholder="Добавить нового поставщика"
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              type="button"
              onClick={() => addExtra()}
              disabled={!newSupplier.trim()}
              className="px-3 py-1.5 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900 disabled:opacity-50"
            >
              + Добавить
            </button>
          </div>
          <SupplierDuplicateHint name={newSupplier} onPick={(n) => addExtra(n)} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по уже сохранённым…"
            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        <div className="flex-1 overflow-auto px-5 py-2">
          {isLoading ? (
            <div className="text-sm text-gray-400 py-4 text-center">Загрузка…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-gray-400 py-4 text-center">
              Поставщиков пока нет — введите имя нового выше.
            </div>
          ) : (
            <ul className="space-y-1">
              {filtered.map((name) => {
                const key = keyOf(name)
                const checked = selected.has(key)
                const isExtra = extras.some((e) => keyOf(e) === key)
                return (
                  <li key={key} className="flex items-center gap-1">
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(name)}
                        className="accent-brand-600"
                      />
                      <span className="text-sm text-gray-800 flex-1 truncate">{name}</span>
                      {isExtra && (
                        <span className="text-[10px] text-brand-600 bg-brand-50 px-1.5 py-0.5 rounded shrink-0">
                          новый
                        </span>
                      )}
                    </label>
                    <button
                      type="button"
                      onClick={() => removeFromList(name)}
                      title="Убрать из списка"
                      className="shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      ×
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 space-y-2">
          {/* Прямой запрос производителю: канал «производитель» по позиции
              эксклюзивен на всю компанию. Занятые коллегой позиции в BOM
              производителю не попадут — придёт отчёт «кто и когда». */}
          <label className="block">
            <span className="text-xs text-gray-500">
              Производитель (прямой запрос — эксклюзив на канал)
            </span>
            <input
              type="text"
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
              placeholder="Имя производителя — отдельный BOM с проверкой занятости"
              className="w-full mt-0.5 px-2 py-1.5 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </label>
          <SupplierDuplicateHint name={manufacturer} onPick={setManufacturer} />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500">
              Выбрано поставщиков: {selected.size}
              {(selected.size > 0 || manufacturer.trim()) &&
                ` · файлов будет ${selected.size + (manufacturer.trim() ? 1 : 0)}`}
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                onClick={() => void downloadNoSupplier()}
                disabled={submitting}
                title="Скачать файл без поставщика — статусы позиций не меняются"
                className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Без поставщика
              </button>
              <button
                onClick={() => void submit()}
                disabled={(selected.size === 0 && !manufacturer.trim()) || submitting}
                className="px-4 py-1.5 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
              >
                {submitting ? 'Создание…' : 'Скачать'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
